import {ReactNode} from "react";
import {List} from "components/common/list/List.component";
import {ListItem} from "components/common/list/ListItem.component";
import {Tag, TagType} from "components/common/Tag.component";
import {useTranslation} from "components/translations/useTranslation";
import {eventBus} from "components/pagelayout/EventBus";
import {Step} from "types/workflowDetailsWizard";
import {StepListItemProps, WorkflowDetailsStepsProps} from "types/newWorkflowDetails";
import {StatusType} from "types/workflowDetailsWizardStep";
import WorkflowHistoryEvent from "./WorkflowHistoryEvent.component";
import CurrentWorkflowStep from "./CurrentWorkflowStep.component";
import {
    APPROVAL_COMPLETED_KEY,
    APPROVAL_INITIATED_KEY,
    assignmentNames,
    completedStatus,
    groupEventsByStep,
    isActiveStep,
    joinNames,
    stepKey,
    stepTag
} from "./workflowDetails.function";

// Expanding a step changes the panel height, so the grid around it has to re-measure
const refreshGridLayout = () => eventBus.dispatch('@@grid/refreshGridLayout');

const StepListItem = (props: StepListItemProps) => {
    const {translate} = useTranslation();
    const hasErrors = !!props.errors && props.errors.length > 0;
    const hasContent = hasErrors || props.events.length > 0 || !!props.currentStep;

    return (
        <ListItem className="workflow-step"
                  dataCy="workflowDetails.step"
                  title={<>
                      <span className="workflow-step__name">{props.name}</span>
                      {props.summary && <span className="workflow-step__summary">{props.summary}</span>}
                  </>}
                  tags={props.tag}
                  toggleAriaLabel={translate("taskDetail.toggleStepDetails", props.name)}
                  isDisabled={!hasContent}
                  defaultExpanded={!!props.currentStep}
                  onExpandedChange={refreshGridLayout}
                  expandedContent={hasContent ?
                      <div className="workflow-step__content">
                          {hasErrors && props.errors!.map((error) =>
                              <div key={error} className="workflow-step__error">{error}</div>)}
                          {props.events.length > 0 &&
                              <List className="workflow-step__events" ariaLabel={translate("taskDetail.workflowHistory")}>
                                  {props.events.map((event, index) =>
                                      <WorkflowHistoryEvent key={`${event.date}-${index}-${event.action}`}
                                                            event={event}
                                                            listedUnderStep={props.stepName}
                                                            uniqueKey={`${props.itemKey}-${index}`}/>)}
                              </List>}
                          {props.currentStep && <CurrentWorkflowStep step={props.currentStep}/>}
                      </div> : null}/>
    );
}

export function WorkflowDetailsSteps({data, events, hideSkippedSteps}: WorkflowDetailsStepsProps) {
    const {translate} = useTranslation();
    const steps = data.process?.steps ?? [];
    const eventsByStep = groupEventsByStep(events, steps);
    const names = (list: Array<string>) => joinNames(list, translate("taskDetail.and"), translate("taskDetail.more"));

    const stepSummary = (step: Step): ReactNode => {
        switch (step.status) {
            case StatusType.PASSED:
            case StatusType.APPROVED:
                return step.completedAssignments.length > 0 &&
                    `${translate("taskDetail.approvedBy")} ${names(assignmentNames(step.completedAssignments))}`;
            case StatusType.ACTIVE:
            case StatusType.CURRENT:
                return step.incompleteAssignments.length > 0 &&
                    `${translate("taskDetail.pendingApprovalBy")} ${names(assignmentNames(step.incompleteAssignments))}`;
            case StatusType.FUTURE:
                return step.incompleteAssignments.length > 0 &&
                    `${translate("taskDetail.tasksFor")} ${names(assignmentNames(step.incompleteAssignments))}`;
            case StatusType.REJECTED:
                return step.lastActionBy ?
                    `${translate("taskDetail.rejectedBy")} ${step.lastActionBy.firstName} ${step.lastActionBy.lastName}` :
                    translate("workflowProcessStatus.REJECTED");
            case StatusType.CANCELED:
                return data.displaySourceApplication &&
                    `${translate("workflowProcessStatus.CANCELLED")} ${translate("taskDetail.from")} ${data.displaySourceApplication}`;
            case StatusType.SKIPPED:
                return step.explanation;
            default:
                return null;
        }
    }

    const tag = (step: Step) => {
        const statusTag = stepTag(step.status);
        return statusTag && <Tag type={statusTag.type} message={translate(statusTag.translationKey)}/>;
    }

    const initiatedBy = data.requester || data.submitter;
    const visibleSteps = steps.filter((step) => !hideSkippedSteps || step.status !== StatusType.SKIPPED);
    const completedEvents = eventsByStep.get(APPROVAL_COMPLETED_KEY) ?? [];
    const endStatus = completedStatus(completedEvents[completedEvents.length - 1]);
    const completedTag = endStatus && tag({status: endStatus} as Step);

    return (
        <List className="workflow-steps" ariaLabel={translate("taskDetail.workflowDetails")}
              dataCy="workflowDetails.steps">
            <StepListItem itemKey={APPROVAL_INITIATED_KEY}
                          name={translate("taskDetail.approvalInitiated")}
                          summary={initiatedBy ?
                              `${initiatedBy.firstName} ${initiatedBy.lastName} ${translate("taskDetail.activatedFrom")} ${data.displaySourceApplication}` :
                              `${translate("taskDetail.activatedFromNoUser")} ${data.displaySourceApplication}`}
                          tag={<Tag type={TagType.SUCCESS} message={translate("taskDetail.activated")}/>}
                          events={eventsByStep.get(APPROVAL_INITIATED_KEY) ?? []}/>

            {/* ListItem reads defaultExpanded only on mount, so a step that becomes (or stops being) the
                active one is remounted to open (or close) it; the other rows keep what the user expanded */}
            {visibleSteps.map((step) =>
                <StepListItem key={`${stepKey(step)}${isActiveStep(step) ? "-active" : ""}`}
                              itemKey={stepKey(step)}
                              name={step.name}
                              stepName={step.name}
                              summary={stepSummary(step)}
                              tag={tag(step)}
                              errors={step.errorExplanations}
                              events={eventsByStep.get(stepKey(step)) ?? []}
                              currentStep={isActiveStep(step) ? step : undefined}/>)}

            {steps.length === 0 && data.status === "CANCELED" &&
                <StepListItem itemKey="no-workflow-canceled"
                              name={translate("workflowProcessStatus.CANCELLED")}
                              summary={null}
                              tag={tag({status: StatusType.CANCELED} as Step)}
                              errors={[translate("taskDetail.noWorkflowDocument")]}
                              events={[]}/>}

            {completedEvents.length > 0 &&
                <StepListItem itemKey={APPROVAL_COMPLETED_KEY}
                              name={translate("taskDetail.approvalCompleted")}
                              summary={null}
                              tag={completedTag}
                              events={completedEvents}/>}
        </List>
    );
}

export default WorkflowDetailsSteps;
